import { FirebaseAnalytics, ConsentType, ConsentStatus } from '@capacitor-firebase/analytics';
import { isNative } from '../utils/platform';
import { safeAnalyticsParameters } from './analyticsPolicy';

export const NATIVE_ANALYTICS_CONSENT = 'finflow_app_analytics_consent';
export function nativeAnalyticsAllowed(): boolean {
  try { return isNative() && localStorage.getItem(NATIVE_ANALYTICS_CONSENT) === 'accepted'; }
  catch { return false; }
}
// Product measurement only. No money, bill contents, user identifiers, or ad audience events.
const allowedEvents = new Set(['sign_up', 'login', 'signup_started', 'signup_form_viewed',
  'onboarding_completed', 'onboarding_viewed', 'screen_view']);
let queue: Promise<void> = Promise.resolve();
function enqueue(work: () => Promise<void>): Promise<void> {
  queue = queue.then(work).catch(() => { console.warn('App analytics unavailable'); });
  return queue;
}
export function syncNativeAnalyticsConsent(): Promise<void> {
  if (!isNative()) return Promise.resolve();
  return enqueue(async () => {
    const enabled = nativeAnalyticsAllowed();
    for (const type of [ConsentType.AdStorage, ConsentType.AdUserData, ConsentType.AdPersonalization]) {
      await FirebaseAnalytics.setConsent({ type, status: ConsentStatus.Denied });
    }
    await FirebaseAnalytics.setConsent({ type: ConsentType.AnalyticsStorage,
      status: enabled ? ConsentStatus.Granted : ConsentStatus.Denied });
    await FirebaseAnalytics.setEnabled({ enabled });
    if (!enabled) await FirebaseAnalytics.resetAnalyticsData();
  });
}
export function trackNativeEvent(name: string, parameters: Record<string, unknown> = {}): Promise<void> {
  if (!nativeAnalyticsAllowed() || !allowedEvents.has(name)) return Promise.resolve();
  return enqueue(async () => {
    // Recheck when queued work executes, including after revocation.
    if (!nativeAnalyticsAllowed()) return;
    const params = safeAnalyticsParameters(parameters);
    if (name === 'screen_view' && ['overview', 'activity', 'plan', 'account', 'onboarding', 'login'].includes(String(parameters.screen_name))) {
      params.screen_name = String(parameters.screen_name);
    }
    await FirebaseAnalytics.logEvent({ name, params });
  });
}
